import * as React from 'react';

import { cn } from '../utils/cn';
import { Card } from './card';
import { Sparkline } from './sparkline';

export type KpiTone = 'brand' | 'ok' | 'warning' | 'rating';
export type KpiChange = { label: string; direction: 'up' | 'down' | 'flat' };

const TONE_SPARK: Record<KpiTone, string> = {
  brand: 'text-[color:var(--brand-500)]',
  ok: 'text-[color:var(--ok)]',
  warning: 'text-amber-600',
  rating: 'text-[color:var(--chart-4)]',
};

const CHANGE_TONE: Record<KpiChange['direction'], string> = {
  up: 'text-[color:var(--ok-fg)]',
  down: 'text-destructive',
  flat: 'text-muted-foreground',
};

export type KpiCardProps = {
  label: string;
  /** Badge / info trigger beside the label. */
  badge?: React.ReactNode;
  value: string;
  /** Muted text right after the value (`/ 100`, stars…). */
  valueSuffix?: React.ReactNode;
  /** Supporting line under the value. */
  sub?: React.ReactNode;
  change?: KpiChange;
  /** Real period series; omit when there is no history. */
  spark?: number[];
  sparkTone?: KpiTone;
  sparkAnimationKey?: string;
  /** Hide the sparkline below `md` (compact mobile tiles). */
  hideSparkOnMobile?: boolean;
  /** Renders the tile as a button. */
  onClick?: () => void;
  className?: string;
};

/**
 * KPI tile — label, headline value, status line and optional sparkline.
 * One implementation for the Status Board and Reputation overview.
 */
export function KpiCard({
  label,
  badge,
  value,
  valueSuffix,
  sub,
  change,
  spark,
  sparkTone = 'brand',
  sparkAnimationKey,
  hideSparkOnMobile = false,
  onClick,
  className,
}: KpiCardProps) {
  const body = (
    <>
      <div className="flex min-w-0 items-center gap-2">
        <span className="text-muted-foreground min-w-0 flex-1 truncate text-[12px] md:text-[13px]">
          {label}
        </span>
        {badge}
      </div>
      {/* Sparkline sits beside the value only, so the status line below gets
          the full tile width instead of wrapping mid-word next to it. */}
      <div className="flex min-w-0 flex-col gap-0.5">
        <div className="flex items-end gap-2 md:gap-2.5">
          <span className="flex min-w-0 flex-1 items-baseline gap-1.5">
            <span className="min-w-0 break-words text-[30px] leading-none font-bold tracking-[-0.4px] text-foreground tabular-nums md:text-[34px]">
              {value}
            </span>
            {valueSuffix ? (
              <span className="text-muted-foreground text-[13px]">
                {valueSuffix}
              </span>
            ) : null}
          </span>
          {spark && spark.length > 0 ? (
            <Sparkline
              values={spark}
              animate={sparkAnimationKey != null}
              animationKey={sparkAnimationKey}
              className={cn(
                'h-[26px] w-14 shrink-0 md:h-9 md:w-24',
                hideSparkOnMobile && 'max-md:hidden',
                TONE_SPARK[sparkTone],
              )}
            />
          ) : null}
        </div>
        {sub ? (
          <span className="text-muted-foreground text-[12px]">{sub}</span>
        ) : null}
        {change ? (
          <span
            className={cn(
              'text-[11px] font-medium tabular-nums',
              CHANGE_TONE[change.direction],
            )}
          >
            {change.label}
          </span>
        ) : null}
      </div>
    </>
  );

  const shell =
    'flex h-full min-w-0 flex-col gap-2 p-3.5 text-left md:gap-2.5 md:p-[18px]';

  return (
    <Card className={cn('min-w-0', className)}>
      {onClick ? (
        <button
          type="button"
          onClick={onClick}
          className={cn(
            shell,
            'w-full rounded-[inherit] transition-colors hover:bg-muted/25 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          )}
        >
          {body}
        </button>
      ) : (
        <div className={shell}>{body}</div>
      )}
    </Card>
  );
}
